import { ErrorMessage } from '@components/error-message'
import { useCurrentUser } from '@features/app-hooks'
import { authModule } from '@features/auth/auth-module'
import { RadioSelect } from '@features/ui-components'
import { OrganizationMembership } from '@st/sdk'
import { Button, FullPageCard, Spinner } from '@st/theme'
import { P, match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { organizationMembershipsModule } from '../organization-memberships-module'
/**
 * After the user is logged in, this component handles:
 * - Fetching the user's organization memberships.
 * - Deciding if we need to display multiple org selection or redirect automatically
 * - Displaying an error or no-orgs message if needed
 */
type Props = {
  onClickCreateOrganization?: () => void
}
export function OrganizationSelectorPage({ onClickCreateOrganization }: Props) {
  const auth = useProcess('auth')
  const user = useCurrentUser()

  return (
    <FullPageCard>
      <OrganizationSelectorBody
        onClickCreateOrganization={onClickCreateOrganization}
        onLogout={() => auth.send({ type: 'logout' })}
        email={user?.email}
      />
    </FullPageCard>
  )
}

function OrganizationSelectorBody({
  onClickCreateOrganization,
  onLogout,
  email
}: {
  onClickCreateOrganization?: () => void
  onLogout: () => void
  email: string | undefined
}) {
  const organizationMemberships = useProcess('organizationMemberships')
  const membershipsState = useSelect('organizationMemberships', (s) => s)

  return (
    match(membershipsState)
      .with({ status: 'idle' }, () => <OrganizationSelectorLoadingState />)
      .with({ status: 'loading' }, () => <OrganizationSelectorLoadingState />)
      .with({ status: 'failed' }, () => (
        <div className="flex flex-col items-center gap-4">
          <ErrorMessage message="Failed to load organizations" />
          <Button
            variant="primary"
            onClick={() => organizationMemberships.send({ type: 'retryLoad' })}
          >
            Try again
          </Button>
        </div>
      ))
      .with(
        { status: 'loaded', memberships: P.when((arr) => arr.length > 1) },
        ({ memberships }) => <OrganizationSelector memberships={memberships} />
      )
      // Redirect is handled in `organizationMembershipsModule` (Elm-style route canonicalization).
      .with({ status: 'loaded', memberships: P.when((arr) => arr.length == 1) }, () => (
        <OrganizationSelectorLoadingState />
      ))
      .otherwise(() => (
        <div className="flex flex-col gap-6">
          <div>
            <h2 className="mb-2 text-center">
              You are not in any organizations. You must create one or be invited to one.
            </h2>
            <p className="text-center text-sm">Logged in as {email}</p>
          </div>

          <div className="flex flex-col gap-2">
            <Button variant="primary" onClick={onLogout}>
              Log out
            </Button>
            {onClickCreateOrganization && (
              <Button variant="default" onClick={onClickCreateOrganization}>
                Create a new organization
              </Button>
            )}
          </div>
        </div>
      ))
  )
}

function OrganizationSelectorLoadingState() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading organizations"
      className="flex h-64 items-center justify-center"
    >
      <Spinner className="size-8 fill-blue-500 text-blue-100" />
      <span className="sr-only">Loading organizations</span>
    </div>
  )
}

/**
 * Allows the user to select which organization they want to access.
 */
function OrganizationSelector({ memberships }: { memberships: OrganizationMembership[] }) {
  const organizationMemberships = useProcess('organizationMemberships')

  type Option = { key: string; label: string }

  const options: Option[] = memberships.map((m) => {
    return { key: m.organization.slug, label: m.organization.name }
  })

  return (
    <div className="flex flex-col gap-10">
      <h2>Please select an organization</h2>

      <RadioSelect
        label="Please select an organization"
        options={options}
        value={undefined}
        onChange={(key) => {
          if (!key) return
          organizationMemberships.send({ type: 'selectOrganization', organizationSlug: key })
        }}
      />
    </div>
  )
}
